/**
 * AccessDenied — inline rendering for 403 errors caught by route handlers.
 *
 * Used when a user with a valid session and the right role hits a 403
 * for a specific resource — almost always a clinic_id outside their
 * scope. Renders as an inline card (not a full-page takeover) because
 * the user can still use the rest of the app; they just can't see this
 * clinic.
 *
 * For role-level access denial (the user authenticated but isn't an AM),
 * see RoleGuard.tsx — that case takes over the whole viewport because
 * there's literally nothing else the user could do here.
 */
import { Link } from "react-router-dom";

export interface AccessDeniedProps {
  /** Clinic the user tried to access (number when known, else null). */
  clinicId?: number | null;
  /** Optional override; defaults to a clinic-scope message. */
  reason?: string;
}

export function AccessDenied({ clinicId, reason }: AccessDeniedProps) {
  return (
    <div className="rounded-lg border border-border bg-white p-6">
      <div className="text-xs font-semibold uppercase tracking-wider text-red-600">
        Access denied
      </div>
      <h2 className="mt-1 text-lg font-semibold text-gray-800">
        {clinicId !== null && clinicId !== undefined
          ? `Clinic ${clinicId} isn't in your scope`
          : "You don't have access to this resource"}
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        {reason ??
          "Your account is signed in, but the resource you tried to open isn't assigned to you. If you should have access, contact your administrator."}
      </p>
      <Link
        to="/clinics"
        className="mt-4 inline-block rounded-button bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
      >
        Back to clinics
      </Link>
    </div>
  );
}
